import { conditionalRender } from '@repo/toolkit/conditional-render'
import { numberOfYearsInLanguage } from '@repo/toolkit/constants/calendar'
import Badge from '@repo/ui/badge'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { getNationalitiesList } from '../../../../api/countries/get-nationalities-list'
import { useEnrollmentStatus } from '../../../../lib/hooks/use-enrollment-status'
import { GenderEnum, genderEnumMapping } from '../../../../lib/types/enums/common/gender.enum'
import {
  SchoolLevelEnum,
  schoolLevelEnumMapping
} from '../../../../lib/types/enums/common/school-level.enum'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import { ChildInformationInput } from '../../enrollment-profile/shared/child-information-form'

type Props = {
  variant?: 'full' | 'partial'
  mode?: 'default' | 'restricted'
  /** absent while the enrollment is being created: there is no stay to check the age against yet */
  enrollmentId?: number
  /** departure date of the stay the age is read on, absent while there is no stay yet */
  departureDate?: string
}

const ChildInformationEnrollmentForm: React.FC<Props> = ({
  variant = 'full',
  mode = 'default',
  enrollmentId,
  departureDate
}) => {
  const form = useFormContext<ChildInformationInput>()

  const { data: enrollmentStatus } = useEnrollmentStatus(enrollmentId)

  const { data: nationalities } = useQuery({
    queryKey: ['nationalities'],
    queryFn: getNationalitiesList
  })

  const birthDate = form.watch('childBirthDate')

  // the backend owns the rule: the badge only renders the verdict it returned
  const isAgeCompliant = enrollmentStatus.isAgeCompliant
  const age = departureDate
    ? EnrollmentProperties.getAgeAtDeparture({
        childBirthDate: birthDate,
        mainProposition: { journey: { date: { departureDate } } }
      })
    : null
  const ageLabel = `Âge : ${age === null ? '-' : `${age} ans`}`

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-10">
        {Object.values(GenderEnum).map((gender) => (
          <FormField
            key={gender}
            control={form.control}
            name="childGender"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={genderEnumMapping('fr')[gender]}
                    checked={field.value === gender}
                    onCheck={() => field.onChange(gender)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        ))}
      </div>

      {variant === 'full' && (
        <div className="flex gap-2">
          <FormField
            disabled={mode === 'restricted'}
            control={form.control}
            name="childLastName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required label={'NOM'} type="text" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            disabled={mode === 'restricted'}
            control={form.control}
            name="childFirstName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required label={'Prénom'} type="text" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 ">
        <div className="flex gap-2">
          <FormField
            control={form.control}
            name="childBirthDate"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    mode="over"
                    label={'Date de naissance'}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <Badge
            size="medium"
            className={`w-fit flex items-center justify-center cursor-not-allowed whitespace-nowrap${
              isAgeCompliant ? '' : ' font-bold'
            }`}
            text={ageLabel}
            variant={isAgeCompliant ? 'grey' : 'danger'}
          />
        </div>

        <FormField
          disabled={mode === 'restricted'}
          control={form.control}
          name="childNationality"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  label={'Nationalité'}
                  searchable
                  dropDownPosition="above"
                  required
                  options={nationalities?.map((nationality) => ({
                    label: nationality.label,
                    value: nationality.code
                  }))}
                  selectedOption={conditionalRender(!!field.value, {
                    label:
                      nationalities?.find((nationality) => nationality.code === field.value)
                        ?.label ?? '',
                    value: field.value
                  })}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div className="flex-1 flex gap-2">
        <FormField
          disabled={mode === 'restricted'}
          control={form.control}
          name="childSchoolLevel"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  required
                  searchable
                  dropDownPosition="above"
                  label={'Niveau scolaire actuel'}
                  options={Object.values(SchoolLevelEnum)?.map((level) => ({
                    label: schoolLevelEnumMapping[level],
                    value: level
                  }))}
                  selectedOption={getSelectedEnumOption(field.value, schoolLevelEnumMapping)}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          disabled={mode === 'restricted'}
          control={form.control}
          name="childNumberOfYearsInLanguage"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  required
                  searchable
                  dropDownPosition="above"
                  label={"Années d'étude de la langue"}
                  options={numberOfYearsInLanguage.map((year) => ({
                    label: year.toString(),
                    value: year
                  }))}
                  selectedOption={conditionalRender(field.value != null, {
                    label: field.value?.toString() ?? '',
                    value: field.value
                  })}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex-1 flex gap-2">
        <FormField
          disabled={mode === 'restricted'}
          control={form.control}
          name="childPhoneNumber"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledPhoneInput
                  {...field}
                  label="N° de portable du jeune"
                  defaultCountry="FR"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          disabled={mode === 'restricted'}
          control={form.control}
          name="childEmail"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput type="email" {...field} label="Email du jeune" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}

export default ChildInformationEnrollmentForm
